import React from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import styles from "./ErrorPage.module.css";
import SpikingAIFooter from "../../components/SpikingAIFooter";

const ErrorPage = ({ data, retryUrl }) => {
  const history = useHistory();

  return (
    <div className={styles.root}>
      <div className={styles.page}>
        <main id="main" className={styles.hero}>
          <section className={styles.copy} aria-labelledby="error-title">
            <p className={styles.statusCode} aria-hidden="true">{data.statusCode}</p>
            <h1 id="error-title">{data.heading}</h1>
            <p className={styles.lead}>{data.lead}</p>

            <div className={styles.actions} aria-label="Recovery actions">
              <a className={`${styles.button} ${styles.primary}`} href="https://spiking.com/system" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Return home
              </a>
              {data.showRetry && (
                <button
                  className={`${styles.button} ${styles.secondary}`}
                  type="button"
                  onClick={() => retryUrl ? history.push(retryUrl) : window.location.reload()}
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v6h-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Retry connection
                </button>
              )}
              <a className={`${styles.button} ${styles.ghost}`} href="https://spi.ke/help" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M21 12a8.5 8.5 0 0 1-8.5 8.5H12a8.5 8.5 0 1 1 8.5-8.5Z" stroke="currentColor" strokeWidth="2.1"/>
                  <path d="M9.8 9.2a2.4 2.4 0 0 1 4.55 1.08c0 1.92-2.35 2.08-2.35 4.02M12 17.2h.01" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"/>
                </svg>
                Contact support
              </a>
            </div>
          </section>

          <section className={styles.visual} aria-label="Spiking protocol status visualization">
            <div className={styles.orbit} aria-hidden="true" />

            <div
              className={styles.terminal}
              role="img"
              aria-label="A premium trading terminal style panel showing protocol status"
            >
              <div className={styles.terminalTop}>
                <span className={styles.traffic} aria-hidden="true">
                  <i /><i /><i />
                </span>
                <span className={styles.terminalTitle}>Protocol Monitor · SpikingAI®</span>
              </div>

              <div className={styles.terminalBody}>
                <div className={styles.chartCard}>
                  <div className={styles.chartHead}>
                    <div>
                      <span>Connection quality</span>
                      <strong>Handshake interrupted</strong>
                    </div>
                    <span className={styles.badge}>{data.statusCode}</span>
                  </div>

                  <svg
                    className={styles.chart}
                    viewBox="0 0 520 180"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id="stroke" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0" stopColor="#1492ff"/>
                        <stop offset="0.56" stopColor="#5ad7ff"/>
                        <stop offset="1" stopColor="#26f5d1"/>
                      </linearGradient>
                      <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#26f5d1" stopOpacity="0.36"/>
                        <stop offset="1" stopColor="#1492ff" stopOpacity="0"/>
                      </linearGradient>
                    </defs>
                    <path className={styles.area} d="M0 142 C44 126 60 94 96 102 C132 110 138 54 180 64 C222 74 235 124 278 104 C322 84 332 30 374 42 C418 54 412 126 458 118 C488 113 503 84 520 78 L520 180 L0 180 Z"/>
                    <path className={styles.line} d="M0 142 C44 126 60 94 96 102 C132 110 138 54 180 64 C222 74 235 124 278 104 C322 84 332 30 374 42 C418 54 412 126 458 118 C488 113 503 84 520 78"/>
                    <circle className={styles.chartError} cx="374" cy="42" r="8"/>
                  </svg>
                </div>

                <div className={styles.metricRow} aria-label="Recovery channels">
                  <div className={styles.metric}>
                    <span>Home</span>
                    <strong>Ready</strong>
                  </div>
                  <div className={styles.metric}>
                    <span>Support</span>
                    <strong>Online</strong>
                  </div>
                  <div className={styles.metric}>
                    <span>Protocol</span>
                    <strong>Mismatch</strong>
                  </div>
                </div>

                <div className={styles.miniRow} aria-label="Platform signals">
                  <div className={styles.miniItem}>
                    <span>Market intelligence</span>
                    <span className={styles.track} aria-hidden="true"><i /></span>
                    <em>active</em>
                  </div>
                  <div className={styles.miniItem}>
                    <span>Portfolio discipline</span>
                    <span className={styles.track} aria-hidden="true"><i /></span>
                    <em>stable</em>
                  </div>
                  <div className={styles.miniItem}>
                    <span>Operating rhythm</span>
                    <span className={styles.track} aria-hidden="true"><i /></span>
                    <em>ready</em>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.ticker} aria-hidden="true">
              <div className={styles.tickerTrack}>
                <span>SpikingAI®</span><span>Insider investors</span><span>Market intelligence</span><span>Risk discipline</span><span>Portfolio execution</span><span>Operating rhythm</span>
                <span>SpikingAI®</span><span>Insider investors</span><span>Market intelligence</span><span>Risk discipline</span><span>Portfolio execution</span><span>Operating rhythm</span>
              </div>
            </div>
          </section>
        </main>

        <SpikingAIFooter />
      </div>
    </div>
  );
};

ErrorPage.propTypes = {
  retryUrl: PropTypes.string,
  data: PropTypes.shape({
    statusCode: PropTypes.string.isRequired,
    statusLabel: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    lead: PropTypes.node.isRequired,
    suggestedFix: PropTypes.string.isRequired,
    terminalCode: PropTypes.string.isRequired,
    terminalNote: PropTypes.string.isRequired,
    showRetry: PropTypes.bool,
  }).isRequired,
};

export default ErrorPage;
